import * as React from 'react';
import styled from '@emotion/styled';
import type { Theme } from '../theme';
import { Icon, type IconName } from './Icon';

export type ToastVariant = 'info' | 'success' | 'warning' | 'danger';

export interface ToastInput {
  variant?: ToastVariant;
  title?: React.ReactNode;
  message?: React.ReactNode;
  /** Auto-dismiss after N ms. Default 3500; pass 0 to keep until dismissed. */
  duration?: number;
}
interface ToastRecord extends ToastInput { id: string; }

interface ToastApi {
  /** Show a toast; returns its id. */
  push: (t: ToastInput) => string;
  /** Dismiss a toast early by id. */
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastApi | null>(null);

/** Access the toast API. Must be used under <ToastProvider>. */
export function useToast(): ToastApi {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

const ICON: Record<ToastVariant, IconName> = { info: 'info', success: 'success', warning: 'warn', danger: 'danger' };
const TONE = (t: Theme): Record<ToastVariant, string> => ({
  info: t.color.primary.base, success: t.color.success, warning: t.color.warning, danger: t.color.danger,
});

const Viewport = styled.div`
  position: fixed;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
  pointer-events: none;
`;
const Card = styled.div`
  pointer-events: auto;
  min-width: 280px;
  max-width: 380px;
  background: ${({ theme }) => theme.color.bg.default};
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-radius: ${({ theme }) => theme.radius.base};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  padding: 12px 14px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  animation: lumen-toast-in 200ms ${({ theme }) => theme.motion.ease};
`;
const Body = styled.div`flex: 1; min-width: 0;`;
const IconTone = styled.span<{ $variant: ToastVariant }>`
  display: inline-flex;
  margin-top: 1px;
  flex-shrink: 0;
  color: ${({ theme, $variant }) => TONE(theme)[$variant]};
`;
const Title = styled.p`font-weight: ${({ theme }) => theme.fontWeight.semibold}; font-size: ${({ theme }) => theme.fontSize.base}; margin: 0;`;
const Msg = styled.p`font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.fg.muted}; margin: 2px 0 0;`;
const Close = styled.button`border: 0; background: none; cursor: pointer; color: ${({ theme }) => theme.color.fg.subtle}; padding: 0;`;

/**
 * Mount once near the app root (inside ThemeProvider). Children can then call
 * `const { push } = useToast()` to fire toasts.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastRecord[]>([]);

  const dismiss = React.useCallback((id: string) => setToasts((list) => list.filter((t) => t.id !== id)), []);
  const push = React.useCallback((input: ToastInput) => {
    const id = Math.random().toString(36).slice(2);
    const duration = input.duration ?? 3500;
    setToasts((list) => [...list, { ...input, id }]);
    if (duration > 0) setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const api = React.useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Viewport>
        {toasts.map((t) => {
          const variant = t.variant ?? 'info';
          return (
            <Card key={t.id} role="status">
              <IconTone $variant={variant}><Icon name={ICON[variant]} size={18} /></IconTone>
              <Body>
                {t.title && <Title>{t.title}</Title>}
                {t.message && <Msg>{t.message}</Msg>}
              </Body>
              <Close onClick={() => dismiss(t.id)} aria-label="Dismiss"><Icon name="x" size={14} /></Close>
            </Card>
          );
        })}
      </Viewport>
    </ToastContext.Provider>
  );
}
